import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@tabipla/ui";
import { SpotForm } from "./SpotForm";
import { useUpdateSpot } from "../../application/hooks/useSpots";
import { showErrorToast } from "../../application/utils/error-toast";
import type { SpotResponseDto } from "@tabipla/shared";

interface EditSpotDialogProps {
  tripId: string;
  spot: SpotResponseDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditSpotDialog({ tripId, spot, open, onOpenChange }: EditSpotDialogProps) {
  const { t } = useTranslation();
  const updateSpot = useUpdateSpot(tripId);

  const handleSubmit = async (data: {
    name: string;
    description: string | null;
    category: "sightseeing" | "food" | "activity" | "transport" | "accommodation";
    startTime: string | null;
    endTime: string | null;
    location: string | null;
    placeId: string | null;
    latitude: number | null;
    longitude: number | null;
    formattedAddress: string | null;
    cost: number | null;
    notes: string | null;
  }) => {
    try {
      await updateSpot.mutateAsync({
        spotId: spot.id,
        input: {
          name: data.name,
          description: data.description,
          category: data.category,
          startTime: data.startTime,
          endTime: data.endTime,
          location: data.location,
          placeId: data.placeId,
          latitude: data.latitude,
          longitude: data.longitude,
          formattedAddress: data.formattedAddress,
          cost: data.cost,
          notes: data.notes,
        },
      });
      toast.success(t("spots.updateSuccess", "スポットを更新しました"));
      onOpenChange(false);
    } catch (error) {
      showErrorToast(error, t);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="pb-2">
          <DialogTitle>{t("spots.editSpot")}</DialogTitle>
          <DialogDescription className="sr-only">{t("spots.editSpot")}</DialogDescription>
        </DialogHeader>
        <SpotForm
          initialData={spot}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isSubmitting={updateSpot.isPending}
        />
      </DialogContent>
    </Dialog>
  );
}
